import { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Building2, ChevronDown, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../auth/context';
import { useTemplateConfig } from '../configContext';
import { WorkspaceContext } from '../workspace/context';
import { filterNavigation, type SidebarItem } from '../lib/sidebar';
import { cn } from '../lib/utils';
import BrandMark from './BrandMark';
import { useT } from '@/i18n';

interface SidebarProps {
  /** When provided, renders a close button in the brand header (drawer mode). */
  onClose?: () => void;
}

function sectionIsActive(item: SidebarItem, pathname: string): boolean {
  if (item.path && pathname.startsWith(item.path)) return true;
  return item.children?.some((child) => child.path && pathname.startsWith(child.path)) ?? false;
}

export default function Sidebar({ onClose }: SidebarProps = {}) {
  const { t } = useT();
  const location = useLocation();
  const { user } = useAuth();
  const config = useTemplateConfig();
  // Always-call hook; conditionally use the value based on config.
  const workspaceCtx = useContext(WorkspaceContext);
  const tenancyEnabled = config.tenancy === 'enabled';
  const workspace = tenancyEnabled ? workspaceCtx : null;

  const userRole = user?.role ?? 'viewer';
  const adminRole = config.adminRole ?? 'admin';

  const visibleNav = useMemo(
    () => filterNavigation(config.navigation, userRole),
    [config.navigation, userRole]
  );

  const [expanded, setExpanded] = useState<string[]>(['workspace']);

  useEffect(() => {
    const autoExpanded = visibleNav
      .filter((item) => item.children && sectionIsActive(item, location.pathname))
      .map((item) => item.id);
    if (autoExpanded.length) {
      setExpanded((previous) => Array.from(new Set([...previous, ...autoExpanded])));
    }
  }, [location.pathname, visibleNav]);

  const toggleExpand = (id: string) => {
    setExpanded((previous) =>
      previous.includes(id) ? previous.filter((entry) => entry !== id) : [...previous, id]
    );
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-[var(--ce-color-border)] bg-[linear-gradient(180deg,#eef2f8_0%,#f5f8fc_22%,#f5f8fc_100%)]">
      <div className="flex items-center justify-between border-b border-[var(--ce-color-border)] px-5 py-5">
        <div className="flex items-center gap-3">
          <BrandMark
            brand={config.brand}
            className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-[var(--ce-color-border)] bg-white p-0.5 shadow-[var(--ce-shadow-soft)]"
            fallbackClassName="rounded-[12px] border-0 bg-[var(--ce-color-ink)] p-0 text-sm font-bold text-white shadow-none"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold tracking-[0.04em] text-[var(--ce-color-ink)]">
              {config.brand.name}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--ce-color-text-subtle)]">
              {t('shell.application', { defaultValue: 'Application shell' })}
            </div>
          </div>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('shell.closeNavigation', { defaultValue: 'Close navigation' })}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--ce-color-text-subtle)] transition-colors hover:bg-white/80 hover:text-[var(--ce-color-ink)]"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {tenancyEnabled && workspace ? (
        <div className="border-b border-[var(--ce-color-border)] px-4 py-4">
          <div className="ce-card-muted px-3 py-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ce-color-text-subtle)]">
              {t('shell.activeWorkspace', { defaultValue: 'Active workspace' })}
            </div>
            {workspace.workspaces.length > 1 ? (
              <select
                value={workspace.activeOrganization?.id ?? ''}
                onChange={(event) => void workspace.switchWorkspace(event.target.value)}
                aria-label={t('shell.activeWorkspace', { defaultValue: 'Active workspace' })}
                className="w-full rounded-[10px] border border-[var(--ce-color-border)] bg-white px-3 py-2 text-sm font-medium text-[var(--ce-color-ink)] outline-none transition-colors focus:border-[var(--ce-color-accent)]"
              >
                {workspace.workspaces.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 rounded-[10px] border border-[var(--ce-color-border)] bg-white px-3 py-2.5 text-sm font-medium text-[var(--ce-color-ink)]">
                <Building2 className="h-4 w-4 shrink-0 text-[var(--ce-color-text-subtle)]" />
                <span className="truncate">
                  {workspace.activeOrganization?.name ?? 'Personal workspace'}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <nav className="flex-1 space-y-1.5 overflow-y-auto px-4 py-4">
        {visibleNav.map((item) => (
          <NavEntry
            key={item.id}
            item={item}
            expanded={expanded}
            pathname={location.pathname}
            onToggle={toggleExpand}
          />
        ))}
      </nav>

      <div className="border-t border-[var(--ce-color-border)] px-4 py-4">
        <div className="ce-card flex items-center gap-3 px-3 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ce-color-ink)] text-sm font-bold text-white">
            {(user?.displayName ?? user?.email ?? 'U').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-[var(--ce-color-ink)]">
              {user?.displayName ?? 'User'}
            </div>
            <div className="truncate text-[11px] uppercase tracking-[0.16em] text-[var(--ce-color-text-subtle)]">
              {user?.role ?? 'viewer'}
              {userRole === adminRole ? ' · admin' : ''}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

interface NavEntryProps {
  item: SidebarItem;
  expanded: string[];
  pathname: string;
  onToggle: (id: string) => void;
}

function NavEntry({ item, expanded, pathname, onToggle }: NavEntryProps) {
  const active = sectionIsActive(item, pathname);

  if (item.children) {
    const isExpanded = expanded.includes(item.id);
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => onToggle(item.id)}
          className={cn(
            'flex w-full items-center justify-between rounded-[12px] px-3 py-2.5 text-sm font-medium transition-colors',
            active
              ? 'bg-white text-[var(--ce-color-ink)] shadow-[var(--ce-shadow-soft)]'
              : 'text-[var(--ce-color-text-subtle)] hover:bg-white/80 hover:text-[var(--ce-color-ink)]'
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
            {item.badge ? (
              <span className="rounded-full bg-[var(--ce-color-accent-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ce-color-ink)]">
                {item.badge}
              </span>
            ) : null}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
        </button>

        {isExpanded ? (
          <div className="space-y-1 overflow-hidden pl-3">
            {item.children.map((child) => (
              <NavLink
                key={child.id}
                to={child.path ?? '#'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-[rgba(82,119,172,0.12)] text-[var(--ce-color-ink)]'
                      : 'text-[var(--ce-color-text-subtle)] hover:bg-white/80 hover:text-[var(--ce-color-ink)]'
                  )
                }
              >
                <child.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{child.label}</span>
                {child.badge ? (
                  <span className="ml-auto rounded-full bg-[var(--ce-color-accent-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ce-color-ink)]">
                    {child.badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path ?? '#'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-[12px] px-3 py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-white text-[var(--ce-color-ink)] shadow-[var(--ce-shadow-soft)]'
            : 'text-[var(--ce-color-text-subtle)] hover:bg-white/80 hover:text-[var(--ce-color-ink)]'
        )
      }
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
      {item.badge ? (
        <span className="ml-auto rounded-full bg-[var(--ce-color-accent-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ce-color-ink)]">
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
}
